import React, { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { supabase } from '../supabaseClient';
import './events.css';
import AsteriskNav from './Asterisknav';
import { Link } from 'react-router-dom';

const Events = () => {
    const navRef = useRef(null);
    const sheetRef = useRef(null);
    const tabsRef = useRef(null);
    const listRef = useRef(null);

    const [activeTab, setActiveTab] = useState('now');
    const [exhibitors, setExhibitors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getExhibitors = async () => {
            const res = await supabase.from('exhibitors').select('*');
            setExhibitors(res.data);
            setLoading(false);
        };
        getExhibitors();
    }, []);

    useEffect(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        gsap.set(navRef.current, { opacity: 0, y: -40 });
        gsap.set(sheetRef.current, { opacity: 0, y: 50, scale: 0.97 });
        gsap.set(tabsRef.current, { opacity: 0, x: -20 });

        tl.to(navRef.current, { opacity: 1, y: 0, duration: 0.8 })
          .to(sheetRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.65 }, '-=0.4')
          .to(tabsRef.current, { opacity: 1, x: 0, duration: 0.5 }, '-=0.3');
    }, []);

    useEffect(() => {
        if (!listRef.current || loading) return;
        const items = listRef.current.querySelectorAll('.tk-card, .tk-empty');
        gsap.fromTo(
            items,
            { opacity: 0, y: 24 },
            { opacity: 1, y: 0, duration: 0.45, stagger: 0.1, ease: 'power3.out' }
        );
    }, [activeTab, loading]);

    const getMonth = (id) => {
        const months = ['May', 'May', 'May', 'May', 'May', 'May'];
        const days = [15, 20, 3, 18, 7, 22];
        const idx = (id - 1) % 6;
        return `${months[idx]} ${days[idx]}`;
    };

    return (
        <div className="home-wrapper">
            <div className="mobile-container">
                <div className="tk-screen">

                    <AsteriskNav active="events" navRef={navRef} />

                    <main className="tk-sheet" ref={sheetRef}>
                        <div className="hp-tab-indicator" aria-hidden="true"></div>

                        <nav className="tk-tabs" ref={tabsRef} aria-label="Event Status Tabs">
                            <button
                                className={`tk-tab${activeTab === 'now' ? ' tk-tab--active' : ''}`}
                                onClick={() => setActiveTab('now')}
                                aria-pressed={activeTab === 'now'}
                            >
                                NOW
                            </button>
                            <button
                                className={`tk-tab${activeTab === 'upcoming' ? ' tk-tab--active' : ''}`}
                                onClick={() => setActiveTab('upcoming')}
                                aria-pressed={activeTab === 'upcoming'}
                            >
                                Next Month
                            </button>
                        </nav>

                        <div className="tk-list" ref={listRef} aria-live="polite">
                            {activeTab === 'now' ? (
                                loading ? (
                                    <div className="tk-loading" aria-label="Loading events">
                                        <div className="tk-loading-bar" aria-hidden="true"></div>
                                        <div className="tk-loading-bar tk-loading-bar--short" aria-hidden="true"></div>
                                    </div>
                                ) : (
                                    exhibitors && exhibitors.map((item) => (
                                        <article key={item.id} className="tk-card-wrapper">
                                            <Link 
                                                to="/details" 
                                                className="no-underline-link" 
                                                aria-label={`View details for ${item.name}`}
                                            >
                                                <div className="tk-card">
                                                    <div className="tk-card-top">
                                                        <div className="tk-card-img-wrap">
                                                            <img src={item.img_ee} alt={`Logo of ${item.name}`} className="tk-card-img" />
                                                        </div>
                                                        <div className="tk-card-info">
                                                            <h3 className="tk-card-name">{item.name}</h3>
                                                            <p className="tk-card-meta">
                                                                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                                                                    <line x1="16" y1="2" x2="16" y2="6"/>
                                                                    <line x1="8" y1="2" x2="8" y2="6"/>
                                                                    <line x1="3" y1="10" x2="21" y2="10"/>
                                                                </svg> <span className="sr-only">Date: </span> {getMonth(item.id)}
                                                            </p>
                                                            <p className="tk-card-meta">
                                                                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                                                    <circle cx="12" cy="10" r="3"/>
                                                                </svg> <span className="sr-only">Location: </span> Cairo Exhibition Center
                                                            </p>
                                                            <span className="tk-card-location"><span className="sr-only">Hall: </span>{item.location_id}</span>
                                                        </div>
                                                        <div className="tk-qr-wrap" aria-hidden="true">
                                                            <svg aria-hidden="true" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <rect x="3" y="3" width="7" height="7" rx="1"/>
                                                                <rect x="14" y="3" width="7" height="7" rx="1"/>
                                                                <rect x="3" y="14" width="7" height="7" rx="1"/>
                                                                <rect x="5" y="5" width="3" height="3" fill="currentColor" stroke="none"/>
                                                                <rect x="16" y="5" width="3" height="3" fill="currentColor" stroke="none"/>
                                                                <rect x="5" y="16" width="3" height="3" fill="currentColor" stroke="none"/>
                                                                <path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3"/>
                                                            </svg>
                                                        </div>
                                                    </div>
                                                    <div className="tk-card-divider" aria-hidden="true"></div>
                                                    <div className="tk-card-bottom">
                                                        <p className="tk-ticket-id">Ticket ID: TDS-{item.id}-{item.location_id}</p>
                                                        <span className="tk-view-btn">VIEW TICKET <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M5 12h14M12 5l7 7-7 7"/>
                                                        </svg></span>
                                                    </div>
                                                </div>
                                            </Link>
                                        </article>
                                    ))
                                )
                            ) : (
                                <section className="tk-empty" aria-labelledby="upcoming-events-title">
                                    <div className="tk-empty-icon" aria-hidden="true">
                                        <svg aria-hidden="true" width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2"/>
                                            <line x1="16" y1="2" x2="16" y2="6"/>
                                            <line x1="8" y1="2" x2="8" y2="6"/>
                                            <line x1="3" y1="10" x2="21" y2="10"/>
                                            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
                                        </svg>
                                    </div>
                                    <h3 className="tk-empty-title" id="upcoming-events-title">Upcoming Events</h3>
                                    <p className="tk-empty-desc">
                                        New events for next month are being prepared. Check back soon for the full schedule.
                                    </p>
                                    <div className="tk-empty-tag" aria-label="Status: Coming Soon">COMING SOON</div>
                                </section>
                            )}
                        </div>

                    </main>
                </div>
            </div>
        </div>
    );
};

export default Events;